// Shared template picker. One TomSelect setup for every place a template is
// chosen as a filter or condition (the job's Template popup, the Metrics
// filter, notification-rule criteria, board automation scope). The <select>
// comes from TemplatePickerHelper#template_picker_select (or the React
// equivalent in AutomationsModal) with:
//
//   - the offered templates as <option>s in "Most used" and "Active"
//     optgroups (data-value most_used / active), each carrying data-jobs,
//     data-recent (jobs inside the window), data-last and data-on-form
//   - an "Inactive" optgroup holding only the already-selected inactive
//     templates (kept), plus data-template-picker-inactive: the rest of
//     the inactive templates as JSON, added to the list once the person
//     has typed two characters and removed again when they stop
//   - data-template-picker-total and -window (days) for the copy
//
// The dropdown opens folded: the Most used group alone, then a "Show N
// more" row for the alphabetical group. Typing searches everything.
const TomSelect = require('./tom_select');

const MONTH_DAY = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const MONTH_DAY_YEAR = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
// With no Most used group (no linked job in the window), the alphabetical
// group folds after this many rows instead.
const FOLD_FALLBACK = 10;

function formatLast(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const date = new Date(y, m - 1, d);
  return (y === new Date().getFullYear() ? MONTH_DAY : MONTH_DAY_YEAR).format(date);
}

function formatCount(n) {
  return Number(n || 0).toLocaleString();
}

function isInactive(data) {
  return Number(data.inactive) === 1;
}

function jobsWord(n) {
  return n === 1 ? 'job' : 'jobs';
}

function usageMeta(data, escape, windowDays) {
  if (data.meta) return escape(data.meta);
  const recent = Number(data.recent || 0);
  const jobs = Number(data.jobs || 0);
  if (recent > 0) {
    return `<b>${formatCount(recent)} ${jobsWord(recent)}</b> in ${windowDays} days · last ${escape(formatLast(data.last))}`;
  }
  if (jobs > 0) return `<b>${formatCount(jobs)} ${jobsWord(jobs)}</b> · last ${escape(formatLast(data.last))}`;
  return 'no jobs yet';
}

export function initTemplatePicker(select, opts = {}) {
  const ds = select.dataset;
  // Rows added by typing need the optgroup the DOM <option>s get for free,
  // or they would render ungrouped, above everything else. The groups are
  // keyed by the <optgroup data-value> (most_used / active / inactive).
  const inactiveRows = JSON.parse(ds.templatePickerInactive || '[]').map((row) => ({ ...row, optgroup: 'inactive' }));
  const total = Number(ds.templatePickerTotal || 0);
  const windowDays = Number(ds.templatePickerWindow || 90);
  const multiple = select.multiple;
  const domOptions = Array.from(select.options);
  const hasBlank = !multiple && domOptions.some((o) => o.value === '');
  const inactiveCount = inactiveRows.length + domOptions.filter((o) => o.dataset.inactive === '1').length;
  let inactiveIn = false;
  let showAll = false;

  const ts = new TomSelect(select, {
    plugins: multiple ? ['remove_button'] : [],
    maxItems: multiple ? null : 1,
    hideSelected: multiple,
    persist: false,
    create: false,
    allowEmptyOption: hasBlank,
    lockOptgroupOrder: true,
    maxOptions: null,
    placeholder: opts.placeholder || ds.placeholder || (multiple ? 'Add a template…' : 'Search or pick a template'),
    searchField: ['text'],
    wrapperClass: 'ts-wrapper tp',
    dropdownClass: 'ts-dropdown tp-dd',
    render: {
      option(data, escape) {
        if (data.value === '') return `<div class="tp-opt tp-opt--blank">${escape(data.text)}</div>`;
        let tag = '';
        if (isInactive(data)) tag = '<span class="tp-tag">Inactive</span>';
        else if (Number(data.onForm) === 1) tag = '<span class="tp-tag tp-tag--on">On form</span>';
        const classes = ['tp-opt'];
        if (isInactive(data)) classes.push('tp-opt--inactive');
        if (!Number(data.jobs) && !data.meta) classes.push('tp-opt--dim');
        return `<div class="${classes.join(' ')}">`
          + `<span class="tp-name">${escape(data.text)}</span>`
          + `<span class="tp-meta">${usageMeta(data, escape, windowDays)}</span>${tag}</div>`;
      },
      item(data, escape) {
        const tag = isInactive(data) ? '<span class="tp-tag">Inactive</span>' : '';
        return `<div class="tp-item${isInactive(data) ? ' tp-item--inactive' : ''}" title="${escape(data.text)}">`
          + `<span class="tp-item-name">${escape(data.text)}</span>${tag}</div>`;
      },
      optgroup_header(data, escape) {
        return `<div class="optgroup-header tp-grp"><span>${escape(data.label)}</span><span class="tp-grp-n"></span></div>`;
      },
      no_results() {
        return '<div class="no-results">No template matches</div>';
      }
    },
    onItemAdd() {
      ts.wrapper.classList.remove('tp-typing');
      if (!multiple) return;
      this.setTextboxValue('');
      this.refreshOptions(false);
    },
    onType(query) {
      syncInactive(query);
      // Single mode: hide the current value while typing over it (TomSelect's
      // own input-active class also covers plain focus, so it cannot be used).
      if (!multiple) ts.wrapper.classList.toggle('tp-typing', String(query || '').trim().length > 0);
    },
    onBlur() {
      ts.wrapper.classList.remove('tp-typing');
    },
    onDropdownClose() {
      showAll = false;
      ts.wrapper.classList.remove('tp-typing');
      syncInactive('');
    }
  });

  // Inactive templates join the option list only while the typed query is
  // two or more characters; selected ones are never removed (kept).
  function syncInactive(query) {
    const wanted = String(query || '').trim().length >= 2;
    if (wanted && !inactiveIn) {
      ts.addOptions(inactiveRows);
      inactiveIn = true;
      ts.refreshOptions(false);
    } else if (!wanted && inactiveIn) {
      ts.clearOptions((option, value) => !isInactive(option) || ts.items.includes(value));
      inactiveIn = false;
      ts.refreshOptions(false);
    }
  }

  function footerText(query) {
    if (!query) {
      if (total < 2) return '';
      return inactiveCount
        ? `Type to search all ${formatCount(total)} templates, including ${formatCount(inactiveCount)} inactive.`
        : `Type to search all ${formatCount(total)} templates.`;
    }
    if (query.length < 2) return inactiveCount ? 'Keep typing to include inactive templates.' : '';
    if (ts.dropdown_content.querySelector('.tp-opt--inactive')) {
      return 'An inactive template can still be picked. It is just not offered until you ask.';
    }
    return '';
  }

  // Fold the alphabetical group behind "Show N more" while nothing is typed:
  // the whole group when Most used has rows, otherwise after FOLD_FALLBACK.
  // TomSelect re-renders from its own option cache on every refresh, so
  // removing rows from the rendered list is safe.
  function fold(content) {
    const activeGroup = content.querySelector('[data-group="active"]');
    if (!activeGroup) return 0;
    const rows = Array.from(activeGroup.querySelectorAll('[data-selectable]'));
    const mostUsed = content.querySelector('[data-group="most_used"] [data-selectable]');
    if (mostUsed) {
      activeGroup.remove();
      return rows.length;
    }
    if (rows.length > FOLD_FALLBACK) {
      rows.slice(FOLD_FALLBACK).forEach((row) => row.remove());
      return rows.length - FOLD_FALLBACK;
    }
    return 0;
  }

  // After each render: the fold, group counts, the footer.
  ts.hook('after', 'refreshOptions', () => {
    const content = ts.dropdown_content;
    const query = ts.inputValue().trim();
    content.querySelectorAll('.tp-more, .tp-foot').forEach((el) => el.remove());

    const hidden = (!query && !showAll) ? fold(content) : 0;

    content.querySelectorAll('.optgroup').forEach((group) => {
      const badge = group.querySelector('.tp-grp-n');
      if (badge) badge.textContent = group.querySelectorAll('[data-selectable]').length;
    });

    if (hidden > 0) {
      const row = document.createElement('div');
      row.className = 'tp-more';
      row.innerHTML = `<span>Show ${formatCount(hidden)} more</span><span>or type to search</span>`;
      row.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showAll = true;
        ts.refreshOptions(false);
      });
      content.appendChild(row);
    }

    const foot = footerText(query);
    if (foot) {
      const el = document.createElement('div');
      el.className = 'tp-foot';
      el.textContent = foot;
      content.appendChild(el);
    }
  });

  // setValue that honors "kept": an inactive template not currently in the
  // option list (for example when a saved automation is loaded for editing)
  // is added first so the selection is not silently dropped.
  ts.setValueKept = (values, silent) => {
    const list = (Array.isArray(values) ? values : [values]).filter((v) => v !== null && v !== undefined).map(String);
    list.forEach((value) => {
      if (ts.options[value]) return;
      const row = inactiveRows.find((r) => String(r.value) === value);
      if (row) ts.addOption(row);
    });
    ts.setValue(multiple ? list : (list[0] || ''), silent);
  };

  return ts;
}

export default initTemplatePicker;
